import React, { useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Gift, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

export default function LoyaltyProgram({ business, onSaved }) {
  const [enabled, setEnabled] = useState(!!business?.loyalty_enabled);
  const [pts, setPts] = useState(business?.loyalty_points_per_purchase ?? 10);
  const [unit, setUnit] = useState(business?.loyalty_purchase_label || "por cada compra");
  const [threshold, setThreshold] = useState(business?.loyalty_threshold ?? 100);
  const [reward, setReward] = useState(business?.loyalty_reward || "");
  const [terms, setTerms] = useState(business?.loyalty_terms || "");
  const [busy, setBusy] = useState(false);

  const save = async (overrides = {}) => {
    setBusy(true);
    try {
      const payload = {
        loyalty_enabled: overrides.loyalty_enabled ?? enabled,
        loyalty_points_per_purchase: Number(pts) || 0,
        loyalty_purchase_label: unit,
        loyalty_threshold: Number(threshold) || 0,
        loyalty_reward: reward,
        loyalty_terms: terms,
      };
      const { data } = await api.put("/business/me", payload);
      onSaved && onSaved(data);
      toast.success(payload.loyalty_enabled ? "Programa de lealtad activado" : "Cambios guardados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (next) => {
    setEnabled(next);
    await save({ loyalty_enabled: next });
  };

  return (
    <div className="grid md:grid-cols-5 gap-6">
      {/* LEFT: form */}
      <div className="md:col-span-3 space-y-5">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#7C3AED]" />
                <h3 className="font-display text-2xl tracking-tight">Programa de lealtad</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Otorga puntos a tus clientes en cada compra y dales una recompensa cuando los acumulen. Independiente de tus ofertas.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={enabled} onCheckedChange={toggle} data-testid="loyalty-toggle" />
              <span className="text-sm font-medium">{enabled ? "Activo" : "Inactivo"}</span>
            </div>
          </div>

          {enabled && (
            <div className="mt-6 space-y-5" data-testid="loyalty-form">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Puntos que otorgas">
                  <Input
                    type="number"
                    min="1"
                    value={pts}
                    onChange={(e) => setPts(e.target.value)}
                    placeholder="10"
                    data-testid="loyalty-points-input"
                  />
                </Field>
                <Field label="Por concepto de">
                  <Input
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="por cada compra · por cada $100 · por visita"
                    data-testid="loyalty-unit-input"
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Puntos para recibir recompensa">
                  <Input
                    type="number"
                    min="1"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    placeholder="100"
                    data-testid="loyalty-threshold-input"
                  />
                </Field>
                <Field label="Recompensa al llegar">
                  <Input
                    value={reward}
                    onChange={(e) => setReward(e.target.value)}
                    placeholder="Un café gratis"
                    data-testid="loyalty-reward-input"
                  />
                </Field>
              </div>

              <Field label="Términos o condiciones (opcional)">
                <Textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={2}
                  placeholder="Ej. Solo válido en sucursal. No acumulable con otras promociones."
                  data-testid="loyalty-terms-input"
                />
              </Field>

              <Button onClick={() => save()} disabled={busy} className="btn-primary rounded-full px-6 py-5" data-testid="loyalty-save-btn">
                <Save className="w-4 h-4 mr-2" />
                {busy ? "Guardando…" : "Guardar configuración"}
              </Button>
            </div>
          )}
        </div>

        <div className="bg-[#F5F3FF] border border-[#C4B5FD] rounded-2xl p-5 text-sm text-zinc-700">
          <div className="flex gap-2">
            <Sparkles className="w-4 h-4 text-[#7C3AED] shrink-0 mt-0.5" />
            <div>
              <strong>Tip:</strong> el programa de lealtad es independiente de tus descuentos y ofertas.
              Puedes ofrecer <em>solo</em> puntos, <em>solo</em> ofertas, o <em>ambos</em>. Tu anuncio mostrará un sello morado cuando esté activo.
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: preview */}
      <div className="md:col-span-2">
        <div className="bg-white border border-border rounded-2xl p-6 sticky top-24">
          <div className="overline text-muted-foreground mb-3">Vista previa del sello</div>
          <div className="rounded-xl border border-dashed border-input p-5 bg-[#FAFAFA]">
            {enabled ? (
              <>
                <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#EDE9FE] to-[#F5F3FF] border border-[#C4B5FD] text-[#5B21B6] text-[11px] px-2.5 py-1 rounded-full font-semibold">
                  <Gift className="w-3 h-3" />
                  Este comercio ofrece puntos por tus compras
                </div>
                <div className="mt-4 text-sm">
                  <div className="font-medium text-[#0E0E12]">Así funcionará tu programa:</div>
                  <ul className="mt-2 space-y-1.5 text-zinc-700 text-sm">
                    <li>• Otorgas <strong>{pts || 0} puntos</strong> {unit || "por cada compra"}</li>
                    <li>• Al acumular <strong>{threshold || 0} puntos</strong> el cliente recibe:</li>
                    <li className="pl-3 text-[#5B21B6] font-medium">🎁 {reward || "—"}</li>
                  </ul>
                  {terms && (
                    <div className="mt-3 text-[11px] text-muted-foreground italic">
                      {terms}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-4">
                Activa el programa para ver cómo se mostrará el sello en tu anuncio.
              </div>
            )}
          </div>

          <div className="mt-4 text-xs text-muted-foreground">
            El sello aparece en una zona pequeña pero visible dentro de tu tarjeta del directorio público.
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
